import axios from 'axios';
import moment from 'moment';
import { pipe } from 'ramda';
import React, { ComponentProps, useCallback } from 'react';
import { DateType, TaskIdV2, UserId } from 'api/models';
import { SourcePage, TimesheetUISourceType } from 'api/timesheets';
import { TimesheetErrors, TimesheetsApi } from 'api/timesheets/timesheets';
import { humanise } from 'components/atoms/humanised-date';
import { toastService } from 'components/molecules/toast';
import { date, DayOfWeek } from 'core/day-of-week';
import { ifPresent, Maybe, orUndefined } from 'core/maybe';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { AddTimeEntryDrawerControlled } from './add-time-entry-drawer-controlled';
import { viewTimesheetButton } from './view-timesheet-button';

interface Props {
  taskId: TaskIdV2;
  userId: UserId;
  entryDate: DateType;
  minutes: Maybe<number>;
  category: Maybe<number>;
  billable: boolean;
  notes: Maybe<string>;
  onClose: () => void;
  roleId: Maybe<string>;
}

export const useSaveCancel = ({
  taskId: [, taskId],
  userId: [, userId],
  entryDate,
  minutes,
  category,
  billable,
  notes,
  onClose,
  roleId,
}: Props): Pick<
  ComponentProps<typeof AddTimeEntryDrawerControlled>,
  'onSave' | 'onCancel'
> => {
  const translate = useTranslate();
  const onSave = useCallback(() => {
    pipe(
      () => minutes,
      ifPresent((duration) => {
        const timesheetDate = moment(entryDate).startOf('day');
        const timesheet = date(DayOfWeek.Monday, timesheetDate);

        const roleIdArr = orUndefined(roleId)?.split('_');

        const data = {
          billable,
          date: timesheetDate.format('YYYY-MM-DD'),
          minutes: duration,
          taskId,
          categoryId: orUndefined(category),
          notes: orUndefined(notes),
          sourcePage: SourcePage.TaskPane,
          sourceId: taskId,
          sourceType: TimesheetUISourceType.Task,
          ...(roleIdArr?.[0] != null ? { roleId: Number(roleIdArr[0]) } : {}),
          ...(roleIdArr?.[1] != null ? { teamId: Number(roleIdArr[1]) } : {}),
        };

        TimesheetsApi.addTaskTimeEntry(UserId(userId), timesheet, data)
          .then((result) => {
            toastService({
              title: translate('task.add_time_entry.success.title'),
              message: translate('task.add_time_entry.success.message', {
                task: <strong>{result.task.name}</strong>,
                action: viewTimesheetButton(translate, entryDate),
              }),
            }).success();
            onClose();
          })
          .catch((error: Error) => {
            logger.error('failed to add time entry', {
              error,
              data,
              userId,
              timesheet: timesheet.format('YYYY-MM-DD'),
            });
            const approvedErrors =
              axios.isAxiosError(error) &&
              (error.response?.data?.errors ?? []).find(
                (x) => x.code === TimesheetErrors.TimesheetApproved,
              ) != null;
            const prefix = `task.add_time_entry.errors.${
              approvedErrors ? 'timesheet_approved' : 'generic'
            }`;

            toastService({
              title: translate(`${prefix}.title` as TranslationKey),
              message: translate(`${prefix}.message` as TranslationKey, {
                reason: error.message ?? '',
                action: viewTimesheetButton(translate, entryDate),
                from: humanise(timesheet),
                to: humanise(timesheet.add(6, 'days')),
              }),
            }).error();
          });
      }),
    )();
  }, [
    taskId,
    entryDate,
    minutes,
    category,
    billable,
    notes,
    userId,
    translate,
    onClose,
    roleId,
  ]);

  return {
    onSave,
    onCancel: onClose,
  };
};
